/**
 * Recording state on the call pages (P7-A design Decision 39). Chime adds the recorder (the media capture pipeline) to
 * the meeting as an attendee, so every participant reads "recording" from the roster: recording while the recorder is
 * present. Kept apart from the React hooks so the decisions can be table-tested (there is no jsdom).
 */
import { isChimeRecorderExternalUserId } from '@unify/fhir-model/resources/chime-attendee';
import type { RecordingNotStartedReason } from './chimeTelehealthService';

/** How long a server-reported change (join, Resume, Stop) holds before the roster alone decides; also the admit wait. */
export const CHIME_RECORDER_SETTLE_MS = 15_000;

/**
 * The recorder attendee ids after one presence event, or undefined when the event is not about the recorder (the
 * caller then treats it as a person). Chime reports the recorder's departure with `dropped` set and may omit the
 * ExternalUserId on leaving, so any departure of a known recorder id counts as "not recording".
 */
export function recorderIdsAfterPresence(
  recorderIds: ReadonlySet<string>,
  attendeeId: string,
  present: boolean,
  externalUserId: string | undefined,
): ReadonlySet<string> | undefined {
  if (!recorderIds.has(attendeeId) && !isChimeRecorderExternalUserId(externalUserId)) return undefined;
  const next = new Set(recorderIds);
  if (present) next.add(attendeeId);
  else next.delete(attendeeId);
  return next;
}

/**
 * Whether the session is recording, as the page shows it. A recorder in the roster always counts (a Stop never hides a
 * recorder that is still there). `expected` is a server answer that recording started (a join's `recordingActive`
 * before media connects, or a Resume) and holds until the roster agrees or the hold passes; `serverRecording` is the
 * latest poll, which also counts a pipeline still starting, so a slow recorder never reads as "not recording".
 */
export function chimeRecordingShown(
  recorderPresent: boolean,
  expected: boolean | undefined,
  serverRecording?: boolean,
): boolean {
  return recorderPresent || expected === true || serverRecording === true;
}

/** Drops the expectation once the roster agrees with it. */
export function settleRecordingExpectation(
  expected: boolean | undefined,
  recorderPresent: boolean,
): boolean | undefined {
  return expected === recorderPresent ? undefined : expected;
}

/** What the provider's `provider-join` said about recording, kept for the admit gate and the recording controls. */
export type ProviderJoinRecording = {
  recordingNotStarted?: RecordingNotStartedReason;
  captureStartedByThisJoin?: boolean;
};

/**
 * No admission gap (Decision 39): the provider's Admit stays disabled until the recording state after their join is
 * settled — at once when the join reported `recordingNotStarted` or did not start a capture; when it did, once the
 * recorder has been seen in the roster, or after {@link CHIME_RECORDER_SETTLE_MS} regardless.
 */
export function providerAdmitReady(input: {
  join: ProviderJoinRecording | undefined;
  recorderSeen: boolean;
  timedOut: boolean;
}): boolean {
  if (!input.join) return false;
  if (input.join.recordingNotStarted || input.join.captureStartedByThisJoin !== true) return true;
  return input.recorderSeen || input.timedOut;
}

/**
 * The recording notice mid-call: the recorder appeared while a client or guest is in the call and they have not
 * accepted the notice in this page session. The server enforces the notice at admission only.
 */
export function midCallRecordingNoticeOpen(input: {
  inCall: boolean;
  recording: boolean;
  acknowledged: boolean;
}): boolean {
  return input.inCall && input.recording && !input.acknowledged;
}

export type RecordingStatusView = {
  /** Shown as the recording badge's tooltip, not as a line of its own. */
  label: string;
  /** Stop recording, Resume recording / Retry (both call `recording-start`), or nothing. */
  control?: { action: 'stop' | 'resume'; label: string };
};

export const RECORDING_NOT_STARTED_TEXT: Record<RecordingNotStartedReason, string> = {
  'consent-missing': "Not recording: a client's consent is not on file",
  'consent-unavailable': 'Not recording: consent could not be verified',
  'start-failed': 'Recording could not start',
};

/**
 * The provider's recording control. Stop while the recorder is present. Otherwise, a join that created the meeting
 * without starting recording says why (consent missing: nothing to offer; consent unavailable or a failed start:
 * Retry); else Resume when the latest `session-status` says a capture ran (`canResume`).
 */
export function providerRecordingView(input: {
  recording: boolean;
  notStarted: RecordingNotStartedReason | undefined;
  canResume: boolean | undefined;
}): RecordingStatusView {
  if (input.recording) return { label: 'Recording', control: { action: 'stop', label: 'Stop recording' } };
  if (input.notStarted === 'consent-missing') return { label: RECORDING_NOT_STARTED_TEXT['consent-missing'] };
  if (input.notStarted) {
    return { label: RECORDING_NOT_STARTED_TEXT[input.notStarted], control: { action: 'resume', label: 'Retry' } };
  }
  if (input.canResume) return { label: 'Recording stopped', control: { action: 'resume', label: 'Resume recording' } };
  return { label: 'Not recording' };
}
